'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "start", "end", "eventId", "occurrenceKey", "renderDragPreview", "id", "isDraggable", "nativeButton"];
import * as React from 'react';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { useId } from '@base-ui/utils/useId';
import { useButton } from "../../base-ui-copy/utils/useButton.mjs";
import { useRenderElement } from "../../base-ui-copy/utils/useRenderElement.mjs";
import { CalendarGridTimeEventCssVars } from "./CalendarGridTimeEventCssVars.mjs";
import { useCalendarGridTimeColumnContext } from "../time-column/CalendarGridTimeColumnContext.mjs";
import { useDraggableEvent } from "../../internals/utils/useDraggableEvent.mjs";
import { useElementPositionInCollection } from "../../internals/utils/useElementPositionInCollection.mjs";
import { getCalendarGridHeaderCellId } from "../../internals/utils/accessibility-utils.mjs";
import { CalendarGridTimeEventContext } from "./CalendarGridTimeEventContext.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { useEventCalendarStoreContext } from "../../use-event-calendar-store-context/index.mjs";
import { schedulerEventSelectors } from "../../scheduler-selectors/index.mjs";
import { useCalendarGridRootContext } from "../root/CalendarGridRootContext.mjs";
import { generateOccurrenceFromEvent } from "../../internals/utils/event-utils.mjs";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const CalendarGridTimeEvent = /*#__PURE__*/React.forwardRef(function CalendarGridTimeEvent(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      start,
      end,
      eventId,
      occurrenceKey,
      renderDragPreview,
      id: idProp,
      isDraggable = false,
      nativeButton = false
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);

  // TODO: Expose a real `interactive` prop
  // to control whether the event should behave like a button
  const isInteractive = true;

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const {
    id: rootId
  } = useCalendarGridRootContext();
  const {
    start: columnStart,
    end: columnEnd,
    index: columnIndex,
    hasFocus: columnHasFocus,
    getCursorPositionInElementMs
  } = useCalendarGridTimeColumnContext();

  // Ref hooks
  const ref = React.useRef(null);

  // State hooks
  const id = useId(idProp);

  // Feature hooks
  const getSharedDragData = useStableCallback(input => {
    const offsetBeforeColumnStart = Math.max(adapter.getTime(columnStart) - start.timestamp, 0);
    const event = schedulerEventSelectors.processedEvent(store.state, eventId);
    const originalOccurrence = generateOccurrenceFromEvent({
      event,
      eventId,
      occurrenceKey,
      start,
      end
    });
    const offsetInsideColumn = getCursorPositionInElementMs({
      input,
      elementRef: ref
    });
    return {
      eventId,
      occurrenceKey,
      originalOccurrence,
      start: start.value,
      end: end.value,
      initialCursorPositionInEventMs: offsetBeforeColumnStart + offsetInsideColumn
    };
  });
  const getDragData = useStableCallback(input => _extends({}, getSharedDragData(input), {
    source: 'CalendarGridTimeEvent'
  }));
  const {
    state,
    preview,
    contextValue: draggableEventContextValue
  } = useDraggableEvent({
    ref,
    start,
    end,
    occurrenceKey,
    eventId,
    isDraggable,
    renderDragPreview,
    getDragData,
    collectionStart: columnStart,
    collectionEnd: columnEnd
  });
  const {
    getButtonProps,
    buttonRef
  } = useButton({
    disabled: !isInteractive,
    native: nativeButton,
    tabIndex: columnHasFocus ? 0 : -1
  });
  const {
    position,
    duration
  } = useElementPositionInCollection({
    start,
    end,
    collectionStart: columnStart,
    collectionEnd: columnEnd
  });
  const columnHeaderId = getCalendarGridHeaderCellId(rootId, columnIndex);
  const contextValue = React.useMemo(() => _extends({}, draggableEventContextValue, {
    getSharedDragData
  }), [draggableEventContextValue, getSharedDragData]);
  const element = useRenderElement('div', componentProps, {
    state,
    ref: [forwardedRef, buttonRef, ref],
    props: [elementProps, {
      id,
      'aria-labelledby': `${columnHeaderId} ${id}`,
      style: {
        [CalendarGridTimeEventCssVars.yPosition]: `${position * 100}%`,
        [CalendarGridTimeEventCssVars.height]: `${duration * 100}%`
      }
    }, getButtonProps]
  });
  return /*#__PURE__*/_jsxs(CalendarGridTimeEventContext.Provider, {
    value: contextValue,
    children: [element, preview.element]
  });
});
if (process.env.NODE_ENV !== "production") CalendarGridTimeEvent.displayName = "CalendarGridTimeEvent";